import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { User, Mail, Key, Save, MapPin } from 'lucide-react';
import CountrySelector from '../ui/CountrySelector';
import { API_BASE_URL } from '../../config/api';;

const Profile = () => {
  const { currentUser, resetPassword, getIdToken } = useAuth();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [country, setCountry] = useState('');
  const navigate = useNavigate();

  // Fetch user profile data when component mounts
  useEffect(() => {
    fetchUserProfile();
  }, [currentUser]);

  const fetchUserProfile = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Fetch user profile from backend
      const response = await fetch(`${API_BASE_URL}/auth/user-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // If endpoint doesn't exist, fall back to just showing the Firebase user info
      if (!response.ok) {
        setUserProfile({
          displayName: currentUser.displayName || '',
          email: currentUser.email || '',
          country: ''
        });
        return;
      }
      
      const profileData = await response.json();
      setUserProfile(profileData);
      setCountry(profileData.country || '');
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Fallback to basic profile info
      setUserProfile({
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        country: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setError('');
      setMessage('');
      setIsSaving(true);
      
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Update user profile in backend
      const response = await fetch(`${API_BASE_URL}/auth/update-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          country: country
        })
      });
      
      // If the endpoint doesn't exist or fails, log it but don't show error to user
      if (!response.ok) {
        console.warn('Backend profile update failed, but continuing');
      } else {
        const data = await response.json();
        console.log('Profile updated successfully:', data);
      }
      
      // Create or update the user document directly in Firestore
      const docResponse = await fetch(`${API_BASE_URL}/auth/create-user-doc`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          country: country
        })
      });
      
      if (!docResponse.ok) {
        console.warn('User document update failed');
      }
      
      setMessage('Profile updated successfully!');
    } catch (error) {
      setError('Failed to update profile: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setMessage('');
      setError('');
      setLoading(true);
      
      if (!currentUser || !currentUser.email) {
        throw new Error('No user email found');
      }
      
      await resetPassword(currentUser.email);
      setMessage('Password reset email sent! Check your inbox.');
    } catch (error) {
      setError('Failed to reset password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b">
          Profile Settings
        </h1>
        
        {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
        {message && <Alert className="border-green-500 bg-green-50 text-green-700 mb-4">{message}</Alert>}
        
        <div className="space-y-6">
          {/* User Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Account Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <div className="bg-gray-100 p-2 rounded mr-4">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Display Name</p>
                  <p className="font-medium">{currentUser?.displayName || 'Not set'}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-gray-100 p-2 rounded mr-4">
                  <Mail className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{currentUser?.email}</p>
                </div>
              </div>
              
              {/* Country Selector */}
              <div className="flex items-start">
                <div className="bg-gray-100 p-2 rounded mr-4">
                  <MapPin className="w-5 h-5 text-gray-500" />
                </div>
                <div className="w-full">
                  <p className="text-sm text-gray-500 mb-1">Country</p>
                  <CountrySelector
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                  />
                </div>
              </div>
              
              {/* Save Button */}
              <div className="flex items-end">
                <Button 
                  onClick={handleUpdateProfile}
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? 'Saving...' : 'Update Profile'}
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Password Management */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Password Management</h2>
            
            <div className="flex items-start">
              <div className="bg-gray-100 p-2 rounded mr-4">
                <Key className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Password</p>
                <p className="text-sm mb-4">
                  Need to change your password? Click the button below to receive a password reset email.
                </p>
                <Button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  {loading ? 'Sending...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Authentication Providers */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Linked Accounts</h2>
            
            <div className="space-y-3">
              {/* Show if Google auth provider is linked */}
              <div className="flex items-center p-3 border rounded-lg">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    d="M12.545 10.239v3.821h5.445c-0.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866 0.549 3.921 1.453l2.814-2.814c-1.79-1.677-4.184-2.702-6.735-2.702-5.514 0-10 4.486-10 10s4.486 10 10 10c5.514 0 10-4.486 10-10 0-0.837-0.12-1.64-0.329-2.411h-9.671z"
                    fill="#4285F4"
                  />
                </svg>
                <span className="font-medium">
                  {currentUser?.providerData?.some(p => p.providerId === 'google.com') 
                    ? 'Connected to Google' 
                    : 'Not connected to Google'}
                </span>
              </div>
              
              {/* Show if Facebook auth provider is linked */}
              <div className="flex items-center p-3 border rounded-lg">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686 0.235 2.686 0.235v2.953h-1.514c-1.491 0-1.956 0.925-1.956 1.874v2.25h3.328l-0.532 3.47h-2.796v8.385c5.737-0.9 10.125-5.864 10.125-11.854z"
                    fill="#1877F2"
                  />
                </svg>
                <span className="font-medium">
                  {currentUser?.providerData?.some(p => p.providerId === 'facebook.com') 
                    ? 'Connected to Facebook' 
                    : 'Not connected to Facebook'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;